/**
 * useCategoryUIState Hook
 * 
 * Manages all UI state for the CategorySettings page:
 * - Dialog open/close states
 * - Form input states
 * - Filter/search states
 * - Accordion expansion states
 * - Editing states
 */

import { useState, useEffect } from "react";
import type React from "react";
import { TransactionCategory, TransactionCategoryGroup, TransactionCategoryDetail } from "../../../types";

export interface UseCategoryUIStateReturn {
  // Dialog states
  addDialogOpen: boolean;
  setAddDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addGroupDialogOpen: boolean;
  setAddGroupDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editDialogOpen: boolean;
  setEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  detailsDialogOpen: boolean;
  setDetailsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  groupDeleteDialogOpen: boolean;
  setGroupDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editGroupDialogOpen: boolean;
  setEditGroupDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  groupDetailsDialogOpen: boolean;
  setGroupDetailsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectDetailsDialogOpen: boolean;
  setSelectDetailsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Editing states (what's being edited)
  editingCategory: TransactionCategory | null;
  setEditingCategory: React.Dispatch<React.SetStateAction<TransactionCategory | null>>;
  categoryForDetails: TransactionCategory | null;
  setCategoryForDetails: React.Dispatch<React.SetStateAction<TransactionCategory | null>>;
  categoryToDelete: TransactionCategory | null;
  setCategoryToDelete: React.Dispatch<React.SetStateAction<TransactionCategory | null>>;
  groupToDelete: TransactionCategoryGroup | null;
  setGroupToDelete: React.Dispatch<React.SetStateAction<TransactionCategoryGroup | null>>;
  editingGroup: TransactionCategoryGroup | null;
  setEditingGroup: React.Dispatch<React.SetStateAction<TransactionCategoryGroup | null>>;
  categoryForSelectDetails: TransactionCategory | null;
  setCategoryForSelectDetails: React.Dispatch<React.SetStateAction<TransactionCategory | null>>;

  // Form state
  categoryName: string;
  setCategoryName: React.Dispatch<React.SetStateAction<string>>;
  categoryGroupId: string;
  setCategoryGroupId: React.Dispatch<React.SetStateAction<string>>;
  groupInputValue: string;
  setGroupInputValue: React.Dispatch<React.SetStateAction<string>>;
  newGroupName: string;
  setNewGroupName: React.Dispatch<React.SetStateAction<string>>;
  editGroupName: string;
  setEditGroupName: React.Dispatch<React.SetStateAction<string>>;
  saving: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  creatingGroup: boolean;
  setCreatingGroup: React.Dispatch<React.SetStateAction<boolean>>;
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  categoryNameError: string;
  setCategoryNameError: React.Dispatch<React.SetStateAction<string>>;
  categoryNameWarning: string;
  setCategoryNameWarning: React.Dispatch<React.SetStateAction<string>>;
  groupNameError: string;
  setGroupNameError: React.Dispatch<React.SetStateAction<string>>;

  // Group details management state
  editingGroupDetails: TransactionCategoryDetail[];
  setEditingGroupDetails: React.Dispatch<React.SetStateAction<TransactionCategoryDetail[]>>;
  originalGroupDetails: TransactionCategoryDetail[];
  setOriginalGroupDetails: React.Dispatch<React.SetStateAction<TransactionCategoryDetail[]>>;
  editingGroupNameForDetails: string;
  setEditingGroupNameForDetails: React.Dispatch<React.SetStateAction<string>>;

  // Details management state (legacy - for category-level details, deprecated)
  editingDetails: TransactionCategoryDetail[];
  setEditingDetails: React.Dispatch<React.SetStateAction<TransactionCategoryDetail[]>>;
  originalDetails: TransactionCategoryDetail[];
  setOriginalDetails: React.Dispatch<React.SetStateAction<TransactionCategoryDetail[]>>;
  newDetailName: string;
  setNewDetailName: React.Dispatch<React.SetStateAction<string>>;
  newDetailType: "list" | "measurable" | "";
  setNewDetailType: React.Dispatch<React.SetStateAction<"list" | "measurable" | "">>;
  newDetailUnit: string;
  setNewDetailUnit: React.Dispatch<React.SetStateAction<string>>;
  newListItems: string[];
  setNewListItems: React.Dispatch<React.SetStateAction<string[]>>;
  editingDetailIndex: number | null;
  setEditingDetailIndex: React.Dispatch<React.SetStateAction<number | null>>;
  editingOriginalDetail: TransactionCategoryDetail | null;
  setEditingOriginalDetail: React.Dispatch<React.SetStateAction<TransactionCategoryDetail | null>>;
  detailNameError: string;
  setDetailNameError: React.Dispatch<React.SetStateAction<string>>;
  savingDetails: boolean;
  setSavingDetails: React.Dispatch<React.SetStateAction<boolean>>;

  // Category detail selection state
  enabledDetails: string[];
  setEnabledDetails: React.Dispatch<React.SetStateAction<string[]>>;
  defaultValues: Record<string, string | number>;
  setDefaultValues: React.Dispatch<React.SetStateAction<Record<string, string | number>>>;
  originalEnabledDetails: string[];
  setOriginalEnabledDetails: React.Dispatch<React.SetStateAction<string[]>>;
  originalDefaultValues: Record<string, string | number>;
  setOriginalDefaultValues: React.Dispatch<React.SetStateAction<Record<string, string | number>>>;

  // Filter state
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  groupSearchTerm: string;
  setGroupSearchTerm: React.Dispatch<React.SetStateAction<string>>;

  // Accordion expansion state
  groupsAccordionExpanded: boolean;
  setGroupsAccordionExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  categoriesAccordionExpanded: boolean;
  setCategoriesAccordionExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  expandedGroupIds: Set<string>;
  setExpandedGroupIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

/**
 * Hook to manage all UI state for CategorySettings page
 */
export function useCategoryUIState(): UseCategoryUIStateReturn {
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [addGroupDialogOpen, setAddGroupDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [groupDeleteDialogOpen, setGroupDeleteDialogOpen] = useState<boolean>(false);
  const [editGroupDialogOpen, setEditGroupDialogOpen] = useState<boolean>(false);
  const [groupDetailsDialogOpen, setGroupDetailsDialogOpen] = useState<boolean>(false);
  const [selectDetailsDialogOpen, setSelectDetailsDialogOpen] = useState<boolean>(false);

  // Editing states (what's being edited)
  const [editingCategory, setEditingCategory] = useState<TransactionCategory | null>(null);
  const [categoryForDetails, setCategoryForDetails] = useState<TransactionCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<TransactionCategory | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<TransactionCategoryGroup | null>(null);
  const [editingGroup, setEditingGroup] = useState<TransactionCategoryGroup | null>(null);
  const [categoryForSelectDetails, setCategoryForSelectDetails] = useState<TransactionCategory | null>(null);

  // Form state
  const [categoryName, setCategoryName] = useState<string>("");
  const [categoryGroupId, setCategoryGroupId] = useState<string>("");
  const [groupInputValue, setGroupInputValue] = useState<string>("");
  const [newGroupName, setNewGroupName] = useState<string>("");
  const [editGroupName, setEditGroupName] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [creatingGroup, setCreatingGroup] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [categoryNameError, setCategoryNameError] = useState<string>("");
  const [categoryNameWarning, setCategoryNameWarning] = useState<string>("");
  const [groupNameError, setGroupNameError] = useState<string>("");

  // Group details management state
  const [editingGroupDetails, setEditingGroupDetails] = useState<TransactionCategoryDetail[]>([]);
  const [originalGroupDetails, setOriginalGroupDetails] = useState<TransactionCategoryDetail[]>([]);
  const [editingGroupNameForDetails, setEditingGroupNameForDetails] = useState<string>("");

  // Details management state (legacy - for category-level details, deprecated)
  const [editingDetails, setEditingDetails] = useState<TransactionCategoryDetail[]>([]);
  const [originalDetails, setOriginalDetails] = useState<TransactionCategoryDetail[]>([]);
  const [newDetailName, setNewDetailName] = useState<string>("");
  const [newDetailType, setNewDetailType] = useState<"list" | "measurable" | "">("");
  const [newDetailUnit, setNewDetailUnit] = useState<string>("");
  const [newListItems, setNewListItems] = useState<string[]>([]);
  const [editingDetailIndex, setEditingDetailIndex] = useState<number | null>(null);
  const [editingOriginalDetail, setEditingOriginalDetail] = useState<TransactionCategoryDetail | null>(null);
  const [detailNameError, setDetailNameError] = useState<string>("");
  const [savingDetails, setSavingDetails] = useState<boolean>(false);

  // Category detail selection state
  const [enabledDetails, setEnabledDetails] = useState<string[]>([]);
  const [defaultValues, setDefaultValues] = useState<Record<string, string | number>>({});
  const [originalEnabledDetails, setOriginalEnabledDetails] = useState<string[]>([]);
  const [originalDefaultValues, setOriginalDefaultValues] = useState<Record<string, string | number>>({});

  // Filter state
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [groupSearchTerm, setGroupSearchTerm] = useState<string>("");

  // Accordion expansion state
  const [groupsAccordionExpanded, setGroupsAccordionExpanded] = useState<boolean>(true);
  const [categoriesAccordionExpanded, setCategoriesAccordionExpanded] = useState<boolean>(true);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());

  return {
    // Dialog states
    addDialogOpen,
    setAddDialogOpen,
    addGroupDialogOpen,
    setAddGroupDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    detailsDialogOpen,
    setDetailsDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    groupDeleteDialogOpen,
    setGroupDeleteDialogOpen,
    editGroupDialogOpen,
    setEditGroupDialogOpen,
    groupDetailsDialogOpen,
    setGroupDetailsDialogOpen,
    selectDetailsDialogOpen,
    setSelectDetailsDialogOpen,

    // Editing states
    editingCategory,
    setEditingCategory,
    categoryForDetails,
    setCategoryForDetails,
    categoryToDelete,
    setCategoryToDelete,
    groupToDelete,
    setGroupToDelete,
    editingGroup,
    setEditingGroup,
    categoryForSelectDetails,
    setCategoryForSelectDetails,

    // Form state
    categoryName,
    setCategoryName,
    categoryGroupId,
    setCategoryGroupId,
    groupInputValue,
    setGroupInputValue,
    newGroupName,
    setNewGroupName,
    editGroupName,
    setEditGroupName,
    saving,
    setSaving,
    creatingGroup,
    setCreatingGroup,
    error,
    setError,
    categoryNameError,
    setCategoryNameError,
    categoryNameWarning,
    setCategoryNameWarning,
    groupNameError,
    setGroupNameError,

    // Group details management state
    editingGroupDetails,
    setEditingGroupDetails,
    originalGroupDetails,
    setOriginalGroupDetails,
    editingGroupNameForDetails,
    setEditingGroupNameForDetails,

    // Details management state (legacy)
    editingDetails,
    setEditingDetails,
    originalDetails,
    setOriginalDetails,
    newDetailName,
    setNewDetailName,
    newDetailType,
    setNewDetailType,
    newDetailUnit,
    setNewDetailUnit,
    newListItems,
    setNewListItems,
    editingDetailIndex,
    setEditingDetailIndex,
    editingOriginalDetail,
    setEditingOriginalDetail,
    detailNameError,
    setDetailNameError,
    savingDetails,
    setSavingDetails,

    // Category detail selection state
    enabledDetails,
    setEnabledDetails,
    defaultValues,
    setDefaultValues,
    originalEnabledDetails,
    setOriginalEnabledDetails,
    originalDefaultValues,
    setOriginalDefaultValues,

    // Filter state
    searchTerm,
    setSearchTerm,
    groupSearchTerm,
    setGroupSearchTerm,

    // Accordion expansion state
    groupsAccordionExpanded,
    setGroupsAccordionExpanded,
    categoriesAccordionExpanded,
    setCategoriesAccordionExpanded,
    expandedGroupIds,
    setExpandedGroupIds,
  };
}
